/**
 * The studio's URL, parsed and written.
 *
 * The shell held its destination in `useState` and nothing ever read
 * `location.pathname`, which was invisible while every screen was reached by
 * clicking the rail — and silently wrong the moment anything outside the studio
 * started handing out links into it. Slack posts
 * `/workflows/:id/runs/:runId` when a run starts, finishes, needs an approval
 * or trips a detection; the builder agent answers a save with
 * `/workflows/:id`; `/knowledge` is in the sentence a step gets told to quote.
 * Every one of those opened the *builder* on whatever workflow happened to be
 * first, because the path was never looked at. The Worker's half was already
 * right — `app.notFound` hands a non-API path to the studio's HTML precisely so
 * a run link resolves — so the missing half was here, and the symptom was a
 * link from Slack that landed on a screen with no run on it.
 *
 * A table and two functions rather than a router: there is one level of
 * navigation, a section within some of the screens, and four screens that take
 * an id, so a routing library would be more code than the thing it replaces.
 * What it has to keep is the round trip — `parseRoute(pathFor(route))` is
 * `route` for every route the studio can be in — because that is what makes
 * the address bar copyable and the back button mean something.
 *
 * **Old paths keep resolving.** The rail was twenty-three destinations and is
 * now ten, with the rest folded in as sections; `/policies`, `/analytics`,
 * `/review` and the others are still in the sentences reef has already sent
 * to Slack threads, so each still opens the screen it used to, inside the
 * section that now holds it. `pathFor` writes the new shape; `parseRoute`
 * reads both.
 */

/** The ten places on the rail, plus the one the CLI sends people to. */
export type Tab =
  | 'home'
  | 'factories'
  | 'work'
  | 'inbox'
  | 'runs'
  | 'build'
  | 'agents'
  | 'connections'
  | 'insights'
  | 'settings'
  /*
   * Where `reef login` sends somebody to approve a terminal.
   *
   * Not on the rail, because nobody navigates to it — the CLI prints the URL
   * and the person follows it. It is in the table all the same: it is a path
   * reef hands out, which is the rule this module exists to keep.
   */
  | 'device';

/**
 * Where a link points, and what the shell is showing.
 *
 * The ids are the parameters of the screens that have any. They are on the
 * route rather than inside the screens because the URL is the only place both
 * the shell and a screen can agree on what is open. `section` is the sub-tab
 * inside a screen that has several — the inbox's three queues, the insights
 * screen's eight reports — and is null where the screen has none.
 */
export interface Route {
  tab: Tab;
  section: string | null;
  workflowId: string | null;
  runId: string | null;
  /**
   * Which connection the Connections screen has open.
   *
   * Reef hands this one out too, and had been dropping it: finishing an OAuth
   * handshake returns to `/connections/:id?linked=1`, so the screen you land on
   * after connecting Linear was whichever connection sorts first — with the
   * "not linked" banner still up, because the connection it described was not
   * the one you had just linked.
   */
  connectionId: string | null;
  factoryId: string | null;
  /**
   * One row within a section, to scroll to and mark.
   *
   * Home lists what is waiting and offers "Open" on each; landing on the queue
   * with the row somewhere below the fold reads as the link having gone to the
   * wrong place. A query parameter rather than a path segment, because the
   * section's path is a link reef already hands out (`/review`, `/knowledge`)
   * and a segment after it would have to be told apart from the section names
   * this table already knows.
   */
  item: string | null;
  /**
   * Which step of the open run has its chat open —
   * `/workflows/:id/runs/:runId/steps/:stepId`.
   *
   * A path segment rather than a query, because it is a *place* the studio can
   * be in and the run's address is already a path: the work board's drawer
   * sends a reader to one step's conversation, and a link that landed on the
   * run with the first step open instead would read as the link having gone
   * to the wrong step. Only meaningful with a `runId`; ignored without one.
   */
  stepId: string | null;
}

/** A route that names a screen and nothing within it. */
export function screenOnly(tab: Tab, section: string | null = null): Route {
  return {
    tab,
    section,
    workflowId: null,
    runId: null,
    connectionId: null,
    factoryId: null,
    item: null,
    stepId: null,
  };
}

/** Landing on `/`, and the answer for a path nothing else claims. */
export const DEFAULT_TAB: Tab = 'home';

/** The sections each screen has, in the order its sub-navigation shows them. */
export const SECTIONS: Partial<Record<Tab, readonly string[]>> = {
  // `all` first: it is what `/inbox` opens, and the three queues stay behind it
  // at the paths (`/review`, `/approvals`, `/knowledge`) reef already sends.
  inbox: ['all', 'review', 'approvals', 'knowledge'],
  // The editor is not a section: it is reached only with a workflow id, at
  // `/workflows/:id`, so `/builder` with nothing after it is the catalogue.
  build: ['catalogue', 'run', 'language'],
  insights: ['analytics', 'goals', 'fleet', 'artifacts', 'audit', 'platform', 'context', 'assistant'],
  settings: ['policies', 'admin', 'operations'],
};

/** The path each rail destination lives at. */
const TAB_PATHS: Record<Tab, string> = {
  home: '/',
  factories: '/factories',
  work: '/work',
  inbox: '/inbox',
  runs: '/runs',
  build: '/builder',
  agents: '/agents',
  connections: '/connections',
  insights: '/insights',
  settings: '/settings',
  device: '/device',
};

/**
 * The paths the studio used to have one rail button for, and where each now
 * lives. Every entry is a link something outside the studio may still hold.
 */
const LEGACY_PATHS: Record<string, { tab: Tab; section: string | null }> = {
  factory: { tab: 'factories', section: null },
  flows: { tab: 'build', section: 'run' },
  workflows: { tab: 'build', section: 'run' },
  builder: { tab: 'build', section: null },
  'build-with-language': { tab: 'build', section: 'language' },
  review: { tab: 'inbox', section: 'review' },
  approvals: { tab: 'inbox', section: 'approvals' },
  knowledge: { tab: 'inbox', section: 'knowledge' },
  // The registry and the roster were two tabs of one screen and are now the
  // one screen; the path stays because it is in Slack threads.
  skills: { tab: 'agents', section: null },
  analytics: { tab: 'insights', section: 'analytics' },
  goals: { tab: 'insights', section: 'goals' },
  fleet: { tab: 'insights', section: 'fleet' },
  artifacts: { tab: 'insights', section: 'artifacts' },
  audit: { tab: 'insights', section: 'audit' },
  platform: { tab: 'insights', section: 'platform' },
  context: { tab: 'insights', section: 'context' },
  assistant: { tab: 'insights', section: 'assistant' },
  policies: { tab: 'settings', section: 'policies' },
  admin: { tab: 'settings', section: 'admin' },
  operations: { tab: 'settings', section: 'operations' },
};

/**
 * Whether a string names a screen.
 *
 * For the routes that arrive over the wire — a search hit carries where it
 * opens as plain strings, because the contract package cannot import `Tab` —
 * so the studio can refuse one it does not know rather than open Home under a
 * run id and call that "found".
 */
export function isTab(value: string): value is Tab {
  return Object.hasOwn(TAB_PATHS, value);
}

const TABS_BY_HEAD = new Map<string, Tab>(
  Object.entries(TAB_PATHS).flatMap(([tab, path]) =>
    path === '/' ? [] : [[path.slice(1), tab as Tab] as const],
  ),
);

/** A section, when the screen has one by that name; the screen's first otherwise. */
function sectionOf(tab: Tab, name: string | undefined): string | null {
  const sections = SECTIONS[tab];
  if (!sections) return null;
  return name && sections.includes(name) ? name : null;
}

/**
 * What a path opens.
 *
 * Unknown paths land on the default tab rather than on a "not found" screen:
 * the studio is one page, everything under this hostname that is not the API is
 * routed here, and a typo in a pasted link is better answered by the app than
 * by an error nobody can act on.
 */
export function parseRoute(pathname: string, search = ''): Route {
  /*
   * The query may arrive glued to the path — `pathFor` writes one string, and
   * the round trip `parseRoute(pathFor(route))` has to hold — or separately,
   * as `location.pathname` and `location.search` come. Both are read; the
   * glued one wins because it is the one this module itself produced.
   */
  const cut = pathname.indexOf('?');
  const path = cut === -1 ? pathname : pathname.slice(0, cut);
  const query = cut === -1 ? search : pathname.slice(cut);
  const item = new URLSearchParams(query).get('item');
  const route = parsePath(path);
  return item ? { ...route, item } : route;
}

function parsePath(pathname: string): Route {
  const segments = pathname.split('/').filter(Boolean).map(decodeSegment);
  const [head, ...rest] = segments;

  if (head === 'workflows') {
    const workflowId = rest[0] ?? null;
    if (!workflowId) return screenOnly('build', 'run');
    // `/workflows/:id/runs/:runId` — the link Slack posts, and the reason this
    // module exists. `/workflows/:id/runs` is the same screen with nothing
    // picked yet, which is what the run list falls back to for a workflow that
    // has never run.
    if (rest[1] === 'runs') {
      const runId = rest[2] ?? null;
      // `…/steps/:stepId` opens that step's chat. Read only under a run — a
      // step id with no run to look it up in names nothing.
      const stepId = runId && rest[3] === 'steps' ? (rest[4] ?? null) : null;
      return { ...screenOnly('runs'), workflowId, runId, stepId };
    }
    // `/workflows/:id/run` — the run form with this workflow already picked,
    // which is where a catalogue row's Run lands. Without the segment the id
    // opens the editor, which is the shape the builder agent hands back.
    if (rest[1] === 'run') return { ...screenOnly('build', 'run'), workflowId };
    return { ...screenOnly('build'), workflowId };
  }

  // `/connections/:id` — where an OAuth handshake comes back to, and what a
  // reader copies out of the address bar to point somebody at the connection
  // they are both looking at.
  if (head === 'connections') {
    return { ...screenOnly('connections'), connectionId: rest[0] ?? null };
  }

  if (head === 'factories') {
    return { ...screenOnly('factories'), factoryId: rest[0] ?? null };
  }

  // `/work/:runId` — a work item is keyed by the run carrying it.
  if (head === 'work') {
    return { ...screenOnly('work'), runId: rest[0] ?? null };
  }

  if (head === undefined) return screenOnly(DEFAULT_TAB);

  const tab = TABS_BY_HEAD.get(head);
  if (tab) return screenOnly(tab, sectionOf(tab, rest[0]));

  // Trailing segments are ignored rather than refused, because the Worker
  // sends `/operations/detections` and that means "that screen" — Operations
  // takes no parameter, so there is nothing for the segment to name.
  const legacy = LEGACY_PATHS[head];
  if (legacy) return screenOnly(legacy.tab, legacy.section);

  return screenOnly(DEFAULT_TAB);
}

/** The path that opens a route again. The inverse of `parseRoute`. */
export function pathFor(route: Route): string {
  const path = pathnameFor(route);
  return route.item ? `${path}?item=${encodeURIComponent(route.item)}` : path;
}

function pathnameFor(route: Route): string {
  if (route.tab === 'runs' && route.workflowId) {
    const base = `/workflows/${encodeURIComponent(route.workflowId)}/runs`;
    if (!route.runId) return base;
    const run = `${base}/${encodeURIComponent(route.runId)}`;
    return route.stepId ? `${run}/steps/${encodeURIComponent(route.stepId)}` : run;
  }
  if (route.tab === 'build' && route.workflowId) {
    const base = `/workflows/${encodeURIComponent(route.workflowId)}`;
    return route.section === 'run' ? `${base}/run` : base;
  }
  if (route.tab === 'connections' && route.connectionId) {
    return `/connections/${encodeURIComponent(route.connectionId)}`;
  }
  if (route.tab === 'factories' && route.factoryId) {
    return `/factories/${encodeURIComponent(route.factoryId)}`;
  }
  if (route.tab === 'work' && route.runId) {
    return `/work/${encodeURIComponent(route.runId)}`;
  }
  const base = TAB_PATHS[route.tab];
  return route.section ? `${base}/${route.section}` : base;
}

/**
 * A path segment, or the segment itself when it is not valid escaping.
 *
 * `decodeURIComponent` throws on a stray `%`, and a link mangled by a chat
 * client that helpfully truncated it would otherwise take down the first render
 * of the whole studio — a blank page instead of the screen the reader asked
 * for.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
